import { Checkbox, Modal, Select } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import {
  NotificationPermissionsDto,
  NotificationSubscriptionDto,
  OrganisationShortType,
  UpdateSubscriptionRequest,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import cn from './EditSubscriptionModal.module.scss'

const PAGE_SIZE = 15

type ClientOption = { value: string; label: string; org: OrganisationShortType }

type Props = {
  subscription: NotificationSubscriptionDto | null
  permissions: NotificationPermissionsDto
  eventLabel?: string
  onClose: () => void
  onUpdated: (sub: NotificationSubscriptionDto) => void
}

function EditSubscriptionModal({
  subscription,
  permissions,
  eventLabel,
  onClose,
  onUpdated,
}: Props) {
  const [modal, contextHolder] = Modal.useModal()

  const [allMyClients, setAllMyClients] = useState(false)
  const [selectedClients, setSelectedClients] = useState<
    OrganisationShortType[]
  >([])

  const [clientOptions, setClientOptions] = useState<ClientOption[]>([])
  const [clientSearchValue, setClientSearchValue] = useState('')
  const [clientLoading, setClientLoading] = useState(false)
  const [clientPage, setClientPage] = useState(0)
  const [clientHasMore, setClientHasMore] = useState(false)
  const clientQueryRef = useRef('')
  const clientDropdownOpen = useRef(false)
  const clientLastFetched = useRef<string | null>(null)

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!subscription) return
    setAllMyClients(subscription.filters?.allMyClients ?? false)
    setSelectedClients(subscription.filters?.clients ?? [])
    setClientOptions([])
    setClientSearchValue('')
    setClientPage(0)
    setClientHasMore(false)
    clientQueryRef.current = ''
    clientLastFetched.current = null
  }, [subscription])

  const fetchClients = useCallback(
    (query: string, page: number, append: boolean) => {
      setClientLoading(true)
      api
        .get(
          `/organisations?description=${encodeURIComponent(query)}&pageSize=${PAGE_SIZE}&page=${page}`
        )
        .then((r) => {
          const content: OrganisationShortType[] =
            r.data.organisations?.content ?? r.data.content ?? []
          const total: number = r.data.organisations?.totalElements ?? 0
          const opts: ClientOption[] = content.map((o) => ({
            value: o.id,
            label: o.description,
            org: o,
          }))
          setClientOptions((prev) => (append ? [...prev, ...opts] : opts))
          setClientHasMore((page + 1) * PAGE_SIZE < total)
          setClientPage(page)
        })
        .catch(() => {})
        .finally(() => setClientLoading(false))
    },
    []
  )

  const handleClientDropdownChange = useCallback(
    (open: boolean) => {
      clientDropdownOpen.current = open
      if (open) {
        clientLastFetched.current = clientQueryRef.current
        fetchClients(clientQueryRef.current, 0, false)
      }
    },
    [fetchClients]
  )

  const handleClientSearch = useCallback(
    (query: string) => {
      if (!clientDropdownOpen.current) return
      setClientSearchValue(query)
      clientQueryRef.current = query
      if (query === clientLastFetched.current) return
      const timer = setTimeout(() => {
        if (clientQueryRef.current === query) {
          clientLastFetched.current = query
          fetchClients(query, 0, false)
        }
      }, 400)
      return () => clearTimeout(timer)
    },
    [fetchClients]
  )

  const handleClientPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLDivElement
    if (
      scrollHeight - scrollTop - clientHeight < 60 &&
      clientHasMore &&
      !clientLoading
    ) {
      fetchClients(clientQueryRef.current, clientPage + 1, true)
    }
  }

  const mergedClientOptions: ClientOption[] = [
    ...selectedClients.map((o) => ({
      value: o.id,
      label: o.description,
      org: o,
    })),
    ...clientOptions.filter(
      (o) => !selectedClients.some((c) => c.id === o.value)
    ),
  ]

  const handleClose = () => onClose()

  const handleSubmit = () => {
    if (!subscription) return

    const body: UpdateSubscriptionRequest = {}

    if (permissions.canUseClientFilters) {
      body.filters = {
        allMyClients,
        clientIds: allMyClients ? [] : selectedClients.map((c) => c.id),
      }
    }

    setSaving(true)
    api
      .put(`/notifications/subscriptions/${subscription.id}`, body)
      .then((r) => {
        onUpdated(r.data.subscription)
        toast.success('Подписка обновлена')
        handleClose()
      })
      .catch((e) => handleApiError(e, modal))
      .finally(() => setSaving(false))
  }

  return (
    <>
      {contextHolder}
      <Modal
        open={!!subscription}
        onCancel={handleClose}
        title={
          subscription
            ? `Редактировать: ${eventLabel ?? subscription.eventType}`
            : 'Редактировать подписку'
        }
        centered
        className={cn.modal}
        footer={null}
        destroyOnHidden
        width={480}
      >
        <div className={cn.body}>
          {/* Фильтры клиентов */}
          {permissions.canUseClientFilters && (
            <section className={cn.section}>
              <h3 className={cn.sectionTitle}>Фильтр клиентов</h3>
              <Checkbox
                checked={allMyClients}
                onChange={(e) => {
                  setAllMyClients(e.target.checked)
                  if (e.target.checked) setSelectedClients([])
                }}
              >
                Применять для всех моих клиентов
              </Checkbox>

              {!allMyClients && (
                <Select
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  placeholder="Поиск организации..."
                  value={selectedClients.map((c) => c.id)}
                  searchValue={clientSearchValue}
                  onSearch={handleClientSearch}
                  onOpenChange={handleClientDropdownChange}
                  onPopupScroll={handleClientPopupScroll}
                  loading={clientLoading}
                  onChange={(ids: string[]) => {
                    setSelectedClients((prev) => {
                      const kept = prev.filter((c) => ids.includes(c.id))
                      const keptIds = new Set(kept.map((c) => c.id))
                      const added = mergedClientOptions
                        .filter(
                          (o) => ids.includes(o.value) && !keptIds.has(o.value)
                        )
                        .map((o) => o.org)
                      return [...kept, ...added]
                    })
                    setClientSearchValue('')
                  }}
                  options={mergedClientOptions.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  notFoundContent={
                    clientLoading ? (
                      <div className={cn.selectLoader}>
                        <Loader size={14} />
                      </div>
                    ) : (
                      <span className={cn.selectEmpty}>Ничего не найдено</span>
                    )
                  }
                  className={cn.fullWidth}
                  maxTagCount="responsive"
                />
              )}
            </section>
          )}

          <div className={cn.actions}>
            <button className={cn.cancelBtn} onClick={handleClose}>
              Отмена
            </button>
            <button
              className={cn.submitBtn}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default EditSubscriptionModal
